<template>
  <div>
    <b-modal size="lg" v-model="showPaymentModal" hide-footer lazy hide-header>

      <div class="d-flex flex-column" v-if="selectedSubscription.plan">
        <div class="d-flex align-items-stretch flex-column flex-lg-row">
          <img :src="planImage(selectedSubscription.plan)" alt="plan logo" class="img-fluid"/>

          <div class="px-2 pt-1 d-flex flex-column flex-grow-1">

            <div class="d-flex flex-row flex-lg-column align-items-center align-items-lg-start py-2 py-lg-0">
              <div class="d-flex align-items-center">
                <div class="ic-photo"></div>
                <div class="ml-2 ic-camera" v-if="selectedSubscription.plan.identifier === 'all_access'"></div>
              </div>

              <h5 class="font-weight-bold m-0 mt-lg-2 ml-2 ml-lg-0">{{ selectedSubscription.plan.product_name }}</h5>
            </div>

            <p class="text-left flex-grow-1">{{ selectedSubscription.plan.description }}</p>

            <div class="d-flex" v-if="!publicPaymentCompleted">
              <b-form-select
                  class="large-bootstrap-select mr-2"
                  v-model="priceSelection[selectedSubscription.plan.id]"
                  @change="selectPlanChanged"
                  :options="priceOptions(selectedSubscription.plan)"/>

              <b-form-select
                  class="large-bootstrap-select"
                  v-model="selectedInterval"
                  :options="calendarOptions"/>
            </div>
            <div class="d-flex justify-content-around" v-else>
              <h6 class="font-weight-semi-bold">Subscription</h6>
              <h6 class="font-weight-semi-bold">{{ selectedSubscription.priceName }}</h6>
              <h6 class="font-weight-semi-bold">{{ selectedSubscriptionIntervalLabel }}</h6>
            </div>
          </div>
        </div>

        <hr class="w-100"/>

        <div class="text-center" v-if="!publicPaymentCompleted">
          <h6 class="font-weight-semi-bold">Today your payment method will be charged</h6>

          <div class="text-dark d-flex align-items-center justify-content-center font-weight-semi-bold">
            <h3 class="m-0 mr-1 font-weight-semi-bold">
              ${{ amountPriceFormatted(priceSelection[selectedSubscription.plan.id]) }}</h3>
            <div> / {{ priceIntervalFormatted(priceSelection[selectedSubscription.plan.id]) }}</div>
          </div>
        </div>
      </div>

      <public-purchase-form
          v-if="!publicPaymentCompleted"
          :selected-subscription="selectedSubscription"
          :agreement="agreement"
          :code="code"
          product-type="subscription"
          @paymentCompleted="handlePaymentCompleted"
          @stripeDiscountChanged="handleStripeDiscountChanged"
          :button-title="buttonTitle"
      />

      <div class="d-flex flex-column align-items-center" v-else>
        <i class="far fa-3x fa-check-circle text-success m-2"></i>
        <h4 class="text-center font-weight-bold">Thank you for <br> your order</h4>
        <p class="text-gray-500 text-center">We've emailed you a receipt to <br> {{ email }}</p>

        <div class="py-3 text-center">
          <p class="font-weight-semi-bold mb-0">Check your email to create your account</p>
          <h4 class="font-weight-bold">{{ selectedSubscription.priceName }} Available</h4>
        </div>

        <p class="text-gray-500">You can Cancel Anytime</p>

        <a href="/search" class="btn btn-primary px-5 py-2 mb-4">Start Your Search</a>
      </div>

      <div class="text-center">
        <h6 class="font-weight-semi-bold">
          By purchasing a subscription, you agree to POCSTOCK's <br/>
          <a class="btn-link font-weight-semi-bold cursor-pointer" @click="agreementModal = true">Subscription Terms</a>,
          <a href="/terms" target="_blank">Terms of Service</a>
          and
          <a href="/legal/privacy" target="_blank">Privacy Policy</a>
        </h6>
      </div>
    </b-modal>

    <b-modal v-model="agreementModal" size="lg" hide-footer title="Subscription Terms">
      <div v-html="agreement" class="pb-3"></div>
    </b-modal>

    <div class="d-flex justify-content-center align-items-center py-3">
      <div :class="{ 'font-weight-semi-bold': selectedInterval === 'month'}" class="mr-2 cursor-pointer"
           @click="selectedInterval = 'month'">
        Billed Monthly
      </div>

      <b-form-checkbox v-model="selectedInterval"
                       size="lg"
                       value="year"
                       unchecked-value="month"
                       switch
                       class="mx-2 mt-n1 control-always-on"/>

      <div>
        <div class="mr-2 cursor-pointer"
             :class="{ 'font-weight-semi-bold': selectedInterval === 'year'}"
             @click="selectedInterval = 'year'">
          Billed Annually
        </div>
        <div class="position-absolute font-size-sm text-primary">Save up to 60%</div>
      </div>
    </div>

    <b-row class="mt-4 align-items-stretch">

      <b-col lg="12">
        <b-row class="align-items-stretch">
          <!-- the plans -->
          <b-col lg="6" v-for="plan in currentPlans" class="mb-3">
            <b-card class="h-100 no-shadow">
              <div class="d-flex justify-content-between flex-column h-100">
                <div>
                  <div class="text-center">
                    <div class="d-flex justify-content-center" style="gap: 10px">
                      <i class="fas fa-images fa-2x" v-b-tooltip title="Photos"></i>
                      <i class="fas fa-video fa-2x" v-b-tooltip title="Videos" v-if="plan.identifier === 'all_access'"></i>
                      <i class="fas fa-pen-nib fa-2x" v-b-tooltip title="Illustrations"/>
                    </div>

                    <h5 class="mt-2 font-weight-bold">{{ plan.product_name }}</h5>

                    <p class="font-weight-semi-bold">Starting at</p>

                    <div class="text-dark d-flex align-items-end justify-content-center">
                      <h1 class="m-0 font-weight-semi-bold">${{ amountPriceFormatted(priceSelection[plan.id]) }}</h1>
                      <div> / {{ priceIntervalFormatted(priceSelection[plan.id]) }}</div>
                    </div>

                    <p class="mt-4 text-left">{{ plan.description }}</p>

                    <b-form-select
                        class="large-bootstrap-select"
                        v-model="priceSelection[plan.id]"
                        :options="priceOptions(plan)"/>
                  </div>

                  <div class="mt-3" v-html="descriptions[plan.identifier]"/>
                </div>

                <b-button
                    v-if="isLoggedIn"
                    variant="primary"
                    class="btn-block px-4 py-2"
                    size="sm"
                    :href="productPriceUrl(priceSelection[plan.id])">
                  CHOOSE PLAN
                </b-button>

                <b-button
                    v-else
                    @click="didSelectPlan(plan)"
                    variant="primary"
                    class="btn-block px-4 py-2"
                    size="sm">
                  CHOOSE PLAN
                </b-button>

              </div>
            </b-card>
          </b-col>

          <!-- on demand -->
          <b-col lg="6" class="mb-3">
            <div class="card h-100 no-shadow">
              <div class="card-body px-2">
                <div class="d-flex justify-content-between flex-column h-100">
                  <div>
                    <div class="text-center">
                      <div class="d-flex justify-content-center" style="gap: 10px">
                        <i class="fas fa-images fa-2x" v-b-tooltip title="Photos"></i>
                        <i class="fas fa-video fa-2x" v-b-tooltip title="Videos"></i>
                        <i class="fas fa-pen-nib fa-2x" v-b-tooltip title="Illustrations"></i>
                      </div>

                      <h5 class="mt-2 font-weight-bold">On Demand</h5>
                      <p class="font-weight-semi-bold">Starting at</p>

                      <div class="text-dark d-flex align-items-end justify-content-center">
                        <h1 class="m-0 font-weight-semi-bold">${{ minOnDemandPrice }}</h1>
                        <div> / asset</div>
                      </div>

                      <p class="mt-4">Pay-as-you-go by credit card</p>
                    </div>

                    <table class="table table-striped table-borderless">
                      <thead>
                      <tr>
                        <th>Photos </th>
                        <th>Videos</th>
                        <th>Illustrations</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="row in pricingMatrix">
                        <td v-for="item in row">
                          <div><b>{{ item.name }}</b></div>
                          <div>{{ item.description }}</div>
                          <h5 class="font-weight-semi-bold">{{ onDemandPlanPrice(item.plan) }}</h5>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>


                </div>
              </div>
            </div>

          </b-col>

          <!-- custom shoots -->
          <b-col lg="6" class="mb-3">
            <div class="card no-shadow">
              <img :src="assignmentsPlaceholderURL" alt=""/>
              <div class="card-body">
                <h5 class="font-weight-semi-bold">Custom Shoots & Assignments</h5>
                <p>
                  Can't find it in stock? Shoot us a brief and we can produce custom photo or video shoots in hundreds
                  of locations around the world.
                </p>
                <b-button variant="primary" class="btn-block px-4 py-2" @click="handleIntercom">
                  Contact Sales
                </b-button>
              </div>
            </div>
          </b-col>

          <!-- agency & corporate -->
          <b-col lg="6" class="mb-3">
            <div class="card no-shadow">
              <img :src="agencyPlaceholderURL" alt=""/>
              <div class="card-body">
                <h5 class="font-weight-semi-bold">Agencies & Corporate Accounts</h5>
                <p>
                  Contact our sales team to setup custom plans for your ad agency or corporation. Pay by invoices and
                  purchase orders.
                </p>
                <b-button variant="primary" class="btn-block px-4 py-2" @click="handleIntercom">
                  Contact Sales
                </b-button>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-col>


    </b-row>
  </div>
</template>

<script>
import {sortBy} from "lodash";
import GeneralMixins from "../../../mixins/GeneralMixins";
import AssetLoader from "../../../../AssetLoader";
import PublicPurchaseForm from "../Assets/PublicPurchaseForm";

export default {
  components: {PublicPurchaseForm},
  props: ['plans', 'descriptions', 'userId', 'commercialProducts', 'editorialProducts', 'agreement', 'code'],
  mixins: [GeneralMixins],

  data() {
    return {
      priceSelection: this.defaultPriceSelection(),
      selectedInterval: 'month',
      showPaymentModal: false,
      selectedSubscription: {},
      publicPaymentCompleted: false,
      agreementModal: false,
      email: null,
      stripeDiscount: null
    }
  },

  watch: {
    showPaymentModal(value) {
      if (!value) {
        this.stripeDiscount = null
      }
    },
    selectedInterval(value) {
      if (this.showPaymentModal && this.selectedSubscription.plan && this.selectedSubscription.plan.identifier) {
        this.didSelectPlan(this.currentPlans.find(plan => plan.identifier === this.selectedSubscription.plan.identifier))
      }
    }
  },

  computed: {

    buttonTitle() {
      if (!this.selectedSubscription.plan) {
        return "Select a Plan"
      }

      return `Start a ${this.selectedSubscription.plan.interval === 'month' ? 'Monthly' : 'Yearly'} Subscription`
    },

    selectedSubscriptionIntervalLabel() {
      if (this.selectedSubscription.plan.identifier === "month") {
        return "Monthly"
      }
      return "Yearly"
    },

    calendarOptions() {
      return [
        {
          value: 'month',
          text: "Monthly"
        },
        {
          value: 'year',
          text: "Yearly"
        }
      ]
    },

    isLoggedIn() {
      return !!window.POC.uid;
    },

    agencyPlaceholderURL() {
      return AssetLoader.imagePath('agencies-placeholder.png');
    },

    assignmentsPlaceholderURL() {
      return AssetLoader.imagePath('assignments-placeholder.png');
    },

    yearlyPlans() {
      return this.plansByInterval('year')
    },

    monthlyPlans() {
      return this.plansByInterval('month')
    },

    currentPlans() {
      if (this.selectedInterval === 'year') {
        return this.yearlyPlans
      } else {
        return this.monthlyPlans
      }
    },

    prices() {
      return this.plans.map(p => p.prices).flatten()
    },

    onDemandVersions() {
      return [
        {
          name: "Small",
          description: "72 ppi photos",
          photoPlan: this.findOnDemandPlan("commercial", "small", "photo"),
          videoPlan: null
        },

        {
          name: "Medium",
          description: "72 ppi photos & HD videos",
          photoPlan: this.findOnDemandPlan("commercial", "medium", "photo"),
          videoPlan: this.findOnDemandPlan("commercial", "medium", "video")
        },

        {
          name: "Large",
          description: "300 ppi photos & 4K videos",
          photoPlan: this.findOnDemandPlan("commercial", "extra_large", "photo"),
          videoPlan: this.findOnDemandPlan("commercial", "large", "video")
        },

        {
          name: "Standard Editorial",
          description: "300 ppi photos & HD videos",
          photoPlan: this.findOnDemandPlan("editorial", "extra_large", "photo"),
          videoPlan: this.findOnDemandPlan("editorial", "medium", "video")
        }
      ]
    },

    pricingMatrix() {
      return [
        [
          //  Row 1, small photos, medium videos and medium illustrations
          {
            name: "Small",
            description: "72 ppi photos",
            plan: this.findOnDemandPlan("commercial", "small", "photo")
          },
          {
            name: "1080p",
            description: "Full HD videos",
            plan: this.findOnDemandPlan("commercial", "medium", "video")
          },
          {
            name: "Graphics",
            description: ".jpg files",
            plan: this.findOnDemandPlan("commercial", "medium", "illustration")
          }
        ],

        [
          // Row 2, medium photos, large videos and large illustrations
          {
            name: "Medium",
            description: "72 ppi photos",
            plan: this.findOnDemandPlan("commercial", "medium", "photo")
          },
          {
            name: "2160p",
            description: "4k videos",
            plan: this.findOnDemandPlan("commercial", "large", "video")
          },
          {
            name: "Vectors",
            description: ".eps, .ai files",
            plan: this.findOnDemandPlan("commercial", "large", "illustration")
          }
        ],

        [
          // Row 3, xl photos, editorial videos
          {
            name: "Large",
            description: "300 ppi photos",
            plan: this.findOnDemandPlan("commercial", "extra_large", "photo")
          },
          {
            name: "Standard Editorial",
            description: "Full HD videos",
            plan: this.findOnDemandPlan("editorial", "medium", "video")
          },
          {}
        ],

        [
          {
            name: "Standard Editorial",
            description: "300 ppi photos",
            plan: this.findOnDemandPlan("editorial", "extra_large", "photo")
          },
          {},
          {}
        ]

      ]
    },

    minOnDemandPrice() {
      // hardcoded for this version. todo find the smallest price
      const price = this.findOnDemandPlan("commercial", "small", "photo")
      if (price) {
        return this.formatPrice(price.cost / 100)
      }
    },


  },

  mounted() {
    let priceSelection = {}
    this.plans.forEach(plan => {
      priceSelection[plan.id] = plan.prices[0].id
    })
  },

  methods: {
    selectPlanChanged(value) {
      // this is a hack since priceSelection doesn't trigger a watch when it gets changed and the selectedSubscription object loses sync
      // this will force it to recreate based on the current priceSelection
      this.didSelectPlan(this.selectedSubscription.plan)
    },

    planImage(plan) {
      if (plan && plan.identifier) {
        return AssetLoader.imagePath(`${plan.identifier}.png`);
      }
    },

    didSelectPlan(plan) {
      const priceId = this.priceSelection[plan.id]
      const amount = `${this.amountPriceFormatted(priceId)}/${this.priceIntervalFormatted(priceId)}`

      this.selectedSubscription = {
        priceId: this.priceSelection[plan.id],
        title: `${plan.product_name} - ${this.getPrice(priceId).name} - $${amount}`,
        plan: plan,
        priceName: this.getPrice(priceId).name
      }

      this.showPaymentModal = true
    },

    handlePaymentCompleted(data) {
      this.email = data.email
      this.publicPaymentCompleted = true
    },

    handleStripeDiscountChanged(discount) {
      this.stripeDiscount = discount
    },

    findOnDemandPlan(usage, versionSize, assetType) {
      const plans = usage === "commercial" ? this.commercialProducts : this.editorialProducts
      return plans.find(plan => this.onDemandPlanHasAssetType(plan, versionSize, assetType))
    },

    onDemandPlanPrice(plan) {
      if (plan) {
        const price = plan.cost / 100
        const formatted = this.formatPrice(price)
        return `$${formatted}`
      }
    },

    onDemandPlanHasAssetType(plan, versionSize, assetType) {
      return plan.product_asset_types.find(p => p.version_size === versionSize && p.asset_type === assetType)
    },

    productPriceUrl(priceID) {
      return `/customers/subscriptions/new?price_id=${priceID}`
    },

    defaultPriceSelection() {
      let priceSelection = {}
      this.plans.forEach(plan => {
        priceSelection[plan.id] = plan.prices[0].id
      })
      return priceSelection
    },

    getPrice(priceID) {
      return this.prices.find(p => p.id === priceID)
    },

    amount(priceID) {
      const price = this.getPrice(priceID)
      return `${this.formatPrice(price.amount / 100)} / ${price.interval}`
    },

    amountPriceFormatted(priceID) {
      let amount = this.getPrice(priceID).amount

      if (this.stripeDiscount) {
        if (this.stripeDiscount.percent_off && this.stripeDiscount.percent_off > 0) {
          amount -= this.stripeDiscount.percent_off / 100 * amount
        } else if (this.stripeDiscount.amount_off && this.stripeDiscount.amount_off > 0) {
          amount -= this.stripeDiscount.amount_off
          if (amount < 0) {
            amount = 0
          }
        }
      }

      return this.formatPrice(amount / 100)
    },

    priceIntervalFormatted(priceID) {
      return `${this.getPrice(priceID).interval}`
    },

    plansByInterval(interval) {
      return sortBy(this.plans.filter(p => p.interval === interval), ['order'])
    },

    priceOptions(plan) {
      return plan.prices.map(price => {
        return {
          value: price.id,
          text: price.name
        }
      })
    },

    handleIntercom() {
      if ('undefined' !== typeof Intercom) {
        Intercom('show')
      }
    }
  }
}
</script>